import { Injectable } from '@angular/core';
import { EfDataPercentColumn } from 'src/app/shared/models/ef-data.model';
import { ButtonExpandGridRenderer } from 'src/app/shared/renderers/button-expand-grid/button-expand-grid.component';
import { DeleteButtonRenderer } from 'src/app/shared/renderers/delete-button/delete-button.component';
import { DropdownListRenderer } from 'src/app/shared/renderers/dropdown-list/dropdown-list.component';
import { InputTextRenderer } from 'src/app/shared/renderers/input-text/input-text.component';
import { ViewButtonRenderer } from 'src/app/shared/renderers/view-button/view-button.component';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { Utils } from 'src/app/shared/utils/utils';

@Injectable({
  providedIn: 'root',
})
export class EFDataColDef {
  public static readonly EXPAND_COLUMN_ID = 'show';
  public static readonly STUDY_OPTIONS_COLUMN_ID = 'studyOptions';

  private readonly studyTypeOptions = EFateConstants.CAKE_STUDY_TYPES.map((studyType: string) => ({ name: studyType }));

  public getDataSetColumnsDefinition(isRowExpanded: boolean, parentInstance: any): any[] {
    return [
      {
        colId: EFDataColDef.EXPAND_COLUMN_ID,
        width: 150,
        minWidth: 150,
        cellRenderer: ButtonExpandGridRenderer,
        cellRendererParams: (params: any) => {
          return {
            showLabel: 'Enter Inputs',
            hideLabel: 'Hide Inputs',
            showIcon: 'pi pi-arrow-down',
            hideIcon: 'pi pi-arrow-right',
            disabled: !this.isDataSetRowComplete(params.data),
          };
        },
        type: 'rightAligned',
        editable: false,
      },
      {
        headerName: '',
        field: 'select',
        type: 'default',
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        editable: false,
        pinned: 'left',
      },
      {
        headerName: 'Study Type',
        field: 'studyType',
        minWidth: 280,
        type: 'default',
        editable: false,
        filter: 'agSetColumnFilter',
        cellRenderer: DropdownListRenderer,
        cellRendererParams: () => {
          return {
            options: this.studyTypeOptions,
            columnThatModifies: 'studyType',
            optionLabel: 'name',
            optionValue: 'name',
            disabled: isRowExpanded,
            isRequired: true,
          };
        },
      },
      {
        colId: EFDataColDef.STUDY_OPTIONS_COLUMN_ID,
        headerName: '',
        field: EFDataColDef.STUDY_OPTIONS_COLUMN_ID,
        width: 170,
        minWidth: 170,
        editable: false,
        filter: false,
        sortable: false,
        cellRenderer: ViewButtonRenderer,
        cellRendererParams: (params: any) => {
          return {
            label: 'Study Options',
            disabled: isRowExpanded || !this.isDataSetRowComplete(params.data),
          };
        },
        type: 'rightAligned',
      },
      {
        headerName: 'Data Set',
        field: 'dataSetName',
        minWidth: 563,
        type: 'default',
        editable: false,
        filter: 'agSetColumnFilter',
        cellRenderer: InputTextRenderer,
        cellRendererParams: () => {
          return {
            disabled: isRowExpanded,
            isRequired: true,
            maxLength: EFateConstants.MAX_DATA_SET_NAME_LENGTH,
          };
        },
      },
      {
        colId: 'delete',
        width: 125,
        minWidth: 125,
        editable: false,
        cellRenderer: DeleteButtonRenderer,
        cellRendererParams: {
          disabled: isRowExpanded,
          parantInstance: parentInstance,
          isDataSetGrid: true,
        },
        type: 'rightAligned',
      },
    ];
  }

  public getExperimentColumnsDefinition(percentColumns: EfDataPercentColumn[], parentInstance: any): any[] {
    const timeColumn = {
      headerName: 'Time (days)',
      field: 'timeDays',
      width: 160,
      minWidth: 160,
      editable: true,
      resizable: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueParser: (params: any) => this.parseDecimal(params.newValue),
    };

    const compoundColumns = percentColumns.map((percentColumn: EfDataPercentColumn) => {
      return {
        headerName: percentColumn.headerName,
        field: percentColumn.field,
        width: 160,
        minWidth: 160,
        editable: true,
        resizable: true,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueParser: (params: any) => this.parseDecimal(params.newValue, EFateConstants.DATA_PERCENT_DECIMALS),
        valueFormatter: (params: any) => Utils.formatFixedDecimals(params.value, EFateConstants.DATA_PERCENT_DECIMALS),
      };
    });

    const deleteColumn = {
      colId: 'delete',
      width: 110,
      minWidth: 110,
      editable: false,
      resizable: false,
      filter: false,
      sortable: false,
      cellRenderer: DeleteButtonRenderer,
      cellRendererParams: {
        parantInstance: parentInstance,
      },
      type: 'rightAligned',
    };

    return [timeColumn, ...compoundColumns, deleteColumn];
  }

  public isDataSetRowComplete(data: any): boolean {
    return `${data?.studyType ?? ''}`.trim() !== '' && `${data?.dataSetName ?? ''}`.trim() !== '';
  }

  public parseDecimal(value: any, decimals?: number): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const text = `${value}`.trim();
    if (text === '') {
      return null;
    }

    let parsed = Number(text);
    if (isNaN(parsed)) {
      parsed = Number(text.replace(',', '.'));
    }

    if (isNaN(parsed)) {
      return null;
    }

    return decimals === undefined ? parsed : Number(parsed.toFixed(decimals));
  }
}
